import { Box, CircularProgress, Typography } from "@mui/material";
import SimpleGridForm from "atoms/Forms/SimpleGridForm";
import { useSettingsData } from "misc/customHooks";
import { PERMISSIONS } from "misc/permissionHelpers";
import { EdmCard } from "molecules";
import PermissionView from "molecules/PermissionView";
import Page from "organisms/Page";
import ListTitle from "organisms/PageTitle/ListTitle";
import { useTranslate } from "react-admin";
import { useParams } from "react-router-dom";

import DefaultsFields from "./DefaultsFields";
import GeneralFields from "./GeneralFields";
import OrderFields from "./OrderFields";
import PortalFields from "./PortalFields";
import StorefrontFields from "./StorefrontFields";

type SettingSection = "general" | "portal" | "defaults" | "storefront" | "orders";

const SECTION_FIELDS: Record<SettingSection, React.ComponentType> = {
  general: GeneralFields,
  portal: PortalFields,
  defaults: DefaultsFields,
  storefront: StorefrontFields,
  orders: OrderFields,
};

export const PORTAL_KEYS = [
  "is_portal_active",
  "public_portal_registration",
  "login_text",
  "custom_theme",
  "days_lecturers_may_change_attendance_after_event",
  "lecturer_booking_export_active",
  "lecturer_emailing_active",
  "cancellation_contact_email",
  "cancellation_period",
  "create_organization_with_account",
];
export const GENERAL_KEYS = [
  "logo_path",
  "favicon_url",
  "imprint_url",
  "data_privacy_url",
  "website_url",
  "default_bcc",
  "frontend_event_base_path",
  "frontend_event_path_segment",
  "read_emails_independent_of_sender",
  "automated_data_deletion_active",
  "automated_data_deletion_user_threshold_days",
  "automated_data_deletion_invoice_threshold_days",
];
export const DEFAULTS_KEYS = ["default_event_location", "default_event_type", "default_price_type"];
export const STOREFRONT_KEYS = ["storefront_config"];
export const ORDER_KEYS = [
  "order_validation_capacity",
  "order_validation_registration_window",
  "order_validation_event_started",
  "order_validation_billing_address",
  "order_checkout_window_minutes",
  "order_manual_processing",
  "order_unverified_user_retention_days",
  "invoice_payment_term_days",
  "invoice_auto_accounting_transfer",
];

export const ALL_SETTINGS_KEYS = [...GENERAL_KEYS, ...PORTAL_KEYS, ...DEFAULTS_KEYS, ...STOREFRONT_KEYS, ...ORDER_KEYS];

const SECTION_KEYS: Record<SettingSection, string[]> = {
  general: GENERAL_KEYS,
  portal: PORTAL_KEYS,
  defaults: DEFAULTS_KEYS,
  storefront: STOREFRONT_KEYS,
  orders: ORDER_KEYS,
};

const isValidSection = (s: string): s is SettingSection => s in SECTION_KEYS;

export default function SettingPage() {
  const { section = "general" } = useParams<{ section: string }>();
  const translate = useTranslate();
  const { loading, defaultValues, handleSave } = useSettingsData();

  const validSection = isValidSection(section) ? section : undefined;
  const SectionFields = validSection ? SECTION_FIELDS[validSection] : undefined;
  const keys = validSection ? SECTION_KEYS[validSection] : [];

  return (
    <Page>
      <PermissionView neededPermissions={[PERMISSIONS.MANAGE_SETTINGS]}>
        <ListTitle backButtonResource="administration" label={translate(`settings.groups.${section}`)} />
        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : SectionFields ? (
          <EdmCard>
            <SimpleGridForm
              defaultValues={defaultValues}
              onSubmit={(v: Record<string, unknown>) => handleSave(keys, v)}
              grid={{ rowSpacing: 4 }}
            >
              <SectionFields />
            </SimpleGridForm>
          </EdmCard>
        ) : (
          <Typography>{translate("ra.navigation.no_results")}</Typography>
        )}
      </PermissionView>
    </Page>
  );
}
